import { Col, Form, Modal, Row } from "react-bootstrap";
import { useState } from "react";
import { useAuth } from "../../../hooks";
import { validateEmail } from "../../../helpers";
import { BiEnvelope, BiLock, BiUser } from "react-icons/bi";
import PhonePrefixSelect, {
  COUNTRY_CODES,
} from "../../cart/_modals/PhonePrefixSelect";

type FormFields = {
  firstname: string;
  lastname: string;
  email: string;
  phone: string;
};

type ValidationErrors = Partial<Record<keyof FormFields, string>>;

type Props = {
  show: boolean;
  handleClose: () => void;
  handleSuccess: () => void;
  handleSwitch: (newMode: any) => void;
};

const GuestModal: React.FC<Props> = ({
  show,
  handleClose,
  handleSuccess,
  handleSwitch,
}) => {
  const { customer, setCustomer } = useAuth();

  const [validated, setValidated] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormFields>({
    firstname: customer?.first_name || "",
    lastname: customer?.last_name || "",
    email: customer?.email || "",
    phone: customer?.phone || "",
  });

  const [errors, setErrors] = useState<ValidationErrors>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let { name, value } = e.target;

    if (["firstname", "lastname"].includes(name))
      value = value.replace(/[^a-zA-Z\s]/g, "");
    else if (["phone"].includes(name)) value = value.replace(/[^0-9]/g, "");

    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" }); // Clear the error when user types
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    setValidated(false);

    if (!formData.firstname) {
      newErrors.firstname = "First name is required";
    }

    if (!formData.email) {
      newErrors.email = "Email address is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    }

    setErrors(newErrors);
    setValidated(true);

    // Return true if there are no errors
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!validateForm()) return false;

    setCustomer({
      id: "0",
      type: "guest",
      first_name: formData.firstname,
      last_name: formData.lastname,
      email: formData.email,
      phone: formData.phone,
    });

    handleSuccess();
  };

  return (
    <>
      <Modal
        className="login modal-mobile-bottom modal-width"
        show={show}
        onHide={handleClose}
        fullscreen="sm-down"
        centered
      >
        <Modal.Header closeButton className="login-modal-header border-0">
          <div className="w-100 text-center">
            <Modal.Title className="login-modal-title">
              Your details
            </Modal.Title>

            <p className="login-modal-subtitle mb-0">
              Please share a few details to continue
            </p>
          </div>
        </Modal.Header>

        <Modal.Body className="login-modal-body pt-0">
          <Form
            className="login_main"
            method="POST"
            validated={validated}
            onSubmit={handleSubmit}
            noValidate
          >
            <Row>
              {/* FIRST NAME */}
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="login-label">First name</Form.Label>

                  <div className="address_icon">
                    <span>
                      <BiUser size={20} />
                    </span>
                    <Form.Control
                      type="text"
                      placeholder="Enter first name"
                      name="firstname"
                      value={formData.firstname}
                      onChange={handleChange}
                      isInvalid={!!errors.firstname}
                      required
                      className="login-input"
                    />
                  </div>

                  {errors.firstname && (
                    <Form.Control.Feedback className="d-block" type="invalid">
                      {errors.firstname}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>

              {/* LAST NAME */}
              <Col xs={12} md={6}>
                <Form.Group className="mb-3">
                  <Form.Label className="login-label">Last name</Form.Label>

                  <div className="address_icon">
                    <span>
                      <BiUser size={20} />
                    </span>
                    <Form.Control
                      type="text"
                      placeholder="Enter last name"
                      name="lastname"
                      value={formData.lastname}
                      onChange={handleChange}
                      isInvalid={!!errors.lastname}
                      className="login-input"
                    />
                  </div>

                  {errors.lastname && (
                    <Form.Control.Feedback className="d-block" type="invalid">
                      {errors.lastname}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>

            {/* PHONE */}
            <Form.Group className="mb-3">
              <Form.Label className="login-label">Phone number</Form.Label>

              <div className="phone_field_wrapper">
                {/* COUNTRY PREFIX */}
                <PhonePrefixSelect value={COUNTRY_CODES[0]} />

                {/* PHONE INPUT */}
                <Form.Control
                  type="tel"
                  placeholder="(201) 555-0123"
                  name="phone"
                  maxLength={10}
                  value={formData.phone}
                  onChange={handleChange}
                  isInvalid={!!errors.phone}
                  required
                  className="login-input phone-control"
                />
                <div className="input-helper-text">
                  We’ll use this to keep you updated about your order.
                </div>
              </div>
              {errors.phone && (
                <Form.Control.Feedback className="d-block" type="invalid">
                  {errors.phone}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* EMAIL */}
            <Form.Group className="mb-4">
              <Form.Label className="login-label">Email address</Form.Label>

              <div className="address_icon">
                <span>
                  <BiEnvelope size={20} />
                </span>
                <Form.Control
                  type="email"
                  placeholder="Enter your email address"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  isInvalid={!!errors.email}
                  required
                  className="login-input"
                />
              </div>

              {errors.email && (
                <Form.Control.Feedback className="d-block" type="invalid">
                  {errors.email}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            {/* CONTINUE BUTTON */}
            <button type="submit" className="login_btn">
              Continue
            </button>

            {/* LOGIN */}
            <div className="modal-bottom-text">
              Already have an account?{" "}
              <span onClick={() => handleSwitch("login")}>Login now</span>
            </div>

            {/* SECURITY TEXT */}
            <div className="auth-security-note">
              <BiLock size={18} className="me-2" />
              We keep your information secure and private
            </div>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default GuestModal;
